/* eslint-disable react/require-default-props */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  Button,
  FeedProgressBar,
  FlexColumn,
  FlexRow,
  ScrollRegion,
  Typography,
  useMaximize,
} from 'design-system';
import {
  ArrowDownIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  CollapseAltIcon,
  ExpandAltIcon,
  LockIcon,
} from 'design-system/components/Icons';
import PdfDocumentViewer from '../../components/PdfDocumentViewer';
import GuidanceStepHeader from '../../components/GuidanceStepHeader';

export default function DocumentStep({
  guidance,
  readPercent,
  documentComplete,
  documentNeedsScroll,
  onScroll,
  onMeasureScroll,
  onContinue,
  canContinue,
}) {
  const intl = useIntl();
  const documentRef = useRef(null);
  const measureFrameRef = useRef(null);
  const contentReadyRef = useRef(false);
  const scrollFractionRef = useRef(null);

  const getScrollElement = () =>
    documentRef.current?.querySelector('.ld-document__scroll') ?? null;

  // Store the position as a fraction of the scrollable range, so restoring it
  // into a different reader height cannot land on the end by itself.
  const captureScrollFraction = () => {
    const element = getScrollElement();
    if (!element) {
      return;
    }

    const maxScroll = element.scrollHeight - element.clientHeight;
    if (maxScroll > 0) {
      scrollFractionRef.current = element.scrollTop / maxScroll;
    }
  };

  const { expanded, toggleExpanded, backdrop } = useMaximize({
    minimizeLabel: intl.formatMessage({ id: 'ld-minimize-document' }),
    onBeforeChange: captureScrollFraction,
  });

  const scheduleMeasureScroll = (element) => {
    if (!element) {
      return;
    }

    if (measureFrameRef.current !== null) {
      cancelAnimationFrame(measureFrameRef.current);
    }

    measureFrameRef.current = requestAnimationFrame(() => {
      measureFrameRef.current = null;
      onMeasureScroll(element);
    });
  };

  const handleContentReady = () => {
    contentReadyRef.current = true;
    scheduleMeasureScroll(getScrollElement());
  };

  useEffect(() => {
    contentReadyRef.current = false;
  }, [guidance.pdfSrc]);

  // The card resizes the reader, so the pages reflow to a new height. Restore
  // the same point in the document and re-measure against the new viewport.
  // Measuring before the pages exist would report a region that needs no
  // scrolling, which counts as fully read.
  useEffect(() => {
    if (!contentReadyRef.current) {
      return;
    }

    const element = getScrollElement();
    if (!element) {
      return;
    }

    if (scrollFractionRef.current !== null) {
      const maxScroll = element.scrollHeight - element.clientHeight;
      if (maxScroll > 0) {
        element.scrollTop = scrollFractionRef.current * maxScroll;
      }
      scrollFractionRef.current = null;
    }

    scheduleMeasureScroll(element);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useEffect(() => () => {
    if (measureFrameRef.current !== null) {
      cancelAnimationFrame(measureFrameRef.current);
    }
  }, []);

  const requireFullCompletion = guidance.require_full_completion ?? true;
  const showStatusNote = documentComplete || requireFullCompletion;
  const showScrollHint =
    requireFullCompletion && documentNeedsScroll && !documentComplete;
  const readLabel = intl.formatMessage({ id: 'ld-read-percent' }, { percent: readPercent });

  return (
    <FlexColumn align="stretch" fullWidth gap="var(--ds-space-6)" className="ld-guidance-step">
      <GuidanceStepHeader
        label={intl.formatMessage(
          { id: 'ld-guidance-of' },
          {
            current: guidance.stepNumber,
            total: guidance.stepTotal,
          },
        )}
        title={guidance.title}
        instruction={guidance.instruction}
      />

      <FlexColumn
        align="stretch"
        fullWidth
        gap="var(--ds-space-2)"
        className={`ld-document-view${expanded ? ' ld-document-view--expanded' : ''}`}
      >
        {/* Expanding only restyles this node — the rendered PDF is never
            remounted, so pages are not re-rendered and read progress holds. */}
        {backdrop}
        <FlexRow align="center" fullWidth gap="var(--ds-space-3)">
          <FeedProgressBar
            value={readPercent}
            showLabel={false}
            trackHeight={4}
            fill="var(--ds-theme-action-primary)"
            aria-valuetext={readLabel}
            className="ld-document-read-progress"
          />
          <Typography
            component="span"
            variant="bodySmall"
            color="ui-subtle"
            className="ld-document-read-progress__label"
          >
            {readLabel}
          </Typography>
          <Button
            type="button"
            variant="tertiary"
            size="base"
            className="ld-document__maximize-btn"
            aria-label={intl.formatMessage({
              id: expanded ? 'ld-minimize-document' : 'ld-maximize-document',
            })}
            aria-pressed={expanded}
            icon={
              expanded ? (
                <CollapseAltIcon size={16} aria-hidden />
              ) : (
                <ExpandAltIcon size={16} aria-hidden />
              )
            }
            onClick={toggleExpanded}
          />
        </FlexRow>

        <FlexColumn align="stretch" fullWidth className="ld-surface ld-document" ref={documentRef}>
          <ScrollRegion className="ld-document__scroll" onScroll={onScroll}>
            <FlexColumn align="start" fullWidth gap="var(--ds-space-4)" className="ld-document__body">
              {guidance.readTimeLabel ? (
                <Typography
                  component="p"
                  variant="bodyExtraSmall"
                  fontWeight="semibold"
                  className="ld-guidance-step__label"
                >
                  {guidance.readTimeLabel}
                </Typography>
              ) : null}
              <PdfDocumentViewer
                src={guidance.pdfSrc}
                title={guidance.title}
                onReady={handleContentReady}
              />
            </FlexColumn>
          </ScrollRegion>
          {documentComplete ? (
            <FlexRow align="center" fullWidth={false} gap="var(--ds-space-2)" className="ld-document__end">
              <CheckCircleIcon size={18} className="ds-icon--color-success" aria-hidden />
              <Typography component="span" variant="bodySmall" fontWeight="semibold" className="ld-guidance-step__success">
                {intl.formatMessage({ id: 'ld-end-of-document' })}
              </Typography>
            </FlexRow>
          ) : null}
          <FlexRow
            align="center"
            justify="center"
            fullWidth={false}
            gap="var(--ds-space-2)"
            className={`ld-document__scroll-hint${showScrollHint ? '' : ' ld-document__scroll-hint--hidden'}`}
            aria-hidden={!showScrollHint}
          >
            <ArrowDownIcon size={16} aria-hidden />
            <Typography component="span" variant="bodySmall" fontWeight="semibold">
              {intl.formatMessage({ id: 'ld-scroll-to-continue' })}
            </Typography>
          </FlexRow>
        </FlexColumn>

        <FlexRow
          align="center"
          justify={showStatusNote ? 'between' : 'end'}
          fullWidth
          wrap
          gap="var(--ds-space-4)"
          className="ld-document-view__footer"
        >
          {showStatusNote && documentComplete ? (
            <FlexRow align="center" fullWidth={false} gap="var(--ds-space-2)">
              <CheckCircleIcon size={18} className="ds-icon--color-success" aria-hidden />
              <Typography component="span" variant="bodySmall" fontWeight="semibold" className="ld-guidance-step__success">
                {intl.formatMessage({ id: 'ld-read-to-end' })}
              </Typography>
            </FlexRow>
          ) : showStatusNote ? (
            <FlexRow align="center" fullWidth={false} gap="var(--ds-space-2)">
              <LockIcon size={16} className="ds-icon--color-muted" aria-hidden />
              <Typography component="span" variant="bodySmall" color="ui-subtle">
                {intl.formatMessage({ id: 'ld-read-to-end-to-continue' })}
              </Typography>
            </FlexRow>
          ) : null}
          <Button
            variant="primary"
            size="base"
            className="ld-guidance-step__continue"
            icon={<ArrowRightIcon size={18} />}
            iconPosition="right"
            disabled={!canContinue}
            onClick={onContinue}
          >
            {intl.formatMessage({ id: 'continue' })}
          </Button>
        </FlexRow>
      </FlexColumn>
    </FlexColumn>
  );
}

DocumentStep.propTypes = {
  guidance: PropTypes.shape({
    label: PropTypes.string.isRequired,
    stepNumber: PropTypes.number.isRequired,
    stepTotal: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    instruction: PropTypes.string.isRequired,
    require_full_completion: PropTypes.bool,
    readTimeLabel: PropTypes.string,
    pdfSrc: PropTypes.string.isRequired,
  }).isRequired,
  readPercent: PropTypes.number.isRequired,
  documentComplete: PropTypes.bool.isRequired,
  documentNeedsScroll: PropTypes.bool.isRequired,
  onScroll: PropTypes.func.isRequired,
  onMeasureScroll: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  canContinue: PropTypes.bool.isRequired,
};
